/**
 * Das Suchfeld mit Treffern (F-18) - im Suchfenster und fest oben in
 * Resources. Treffer erscheinen schon beim Tippen; Pfeiltasten und Enter
 * waehlen, ein Klick fuehrt direkt zum Eintrag.
 */
import { useId, useState } from 'react';
import { Search } from 'lucide-react';

import { useSuchIndex } from '@/modules/suche/useSuchIndex';
import { useTrefferOeffnen } from '@/modules/suche/useTrefferOeffnen';
import { SUCH_ARTEN, suchen, type SuchArt, type Stueck } from '@/domain/suche';
import { cn } from '@/lib/utils/cn';

/** So viele Treffer stehen da - wer mehr braucht, grenzt ein. */
const MAX = 60;

export function SuchPanel({ start = '', autoFocus = false, onGewaehlt, platzhalter = 'Alles durchsuchen – #schlagwort sucht Schlagwörter' }: {
  start?: string;
  autoFocus?: boolean;
  /** Nach dem Oeffnen eines Treffers - z. B. das Suchfenster schliessen. */
  onGewaehlt?: () => void;
  platzhalter?: string;
}) {
  const [query, setQuery] = useState(start);
  const [art, setArt] = useState<SuchArt | null>(null);
  const [ohneErledigte, setOhneErledigte] = useState(false);
  const [aktiv, setAktiv] = useState(0);
  const index = useSuchIndex(query.trim() !== '');
  const oeffnen = useTrefferOeffnen();
  const listeId = useId();

  const ergebnis = index && query.trim() ? suchen(index, query, { arten: art ? [art] : [], erledigteAusblenden: ohneErledigte }) : null;
  const treffer = ergebnis?.treffer.slice(0, MAX) ?? [];
  const gewaehlt = Math.min(aktiv, Math.max(0, treffer.length - 1));

  function waehle(i: number) {
    const t = treffer[i];
    if (!t) return;
    oeffnen(t.eintrag.ziel);
    onGewaehlt?.();
  }

  return (
    <div className="flex flex-col gap-2.5">
      <div className="relative">
        <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-faint" />
        {/* Kein type="search": dort leert Escape nur das Feld, statt das Suchfenster zu schliessen. */}
        <input
          type="text"
          enterKeyHint="search"
          value={query}
          autoFocus={autoFocus}
          onChange={(e) => { setQuery(e.target.value); setAktiv(0); }}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setAktiv((i) => Math.min(i + 1, treffer.length - 1)); }
            if (e.key === 'ArrowUp') { e.preventDefault(); setAktiv((i) => Math.max(i - 1, 0)); }
            if (e.key === 'Enter') { e.preventDefault(); waehle(gewaehlt); }
          }}
          placeholder={platzhalter}
          aria-label="Suche"
          role="combobox"
          aria-expanded={treffer.length > 0}
          aria-controls={listeId}
          aria-activedescendant={treffer.length ? `${listeId}-${gewaehlt}` : undefined}
          className="h-10 w-full rounded-md border border-line bg-surface pl-9 pr-3 text-sm text-ink placeholder:text-ink-faint focus:border-accent focus:outline-none"
        />
      </div>

      {ergebnis && (
        <>
          <div className="flex flex-wrap items-center gap-1.5">
            <Chip aktiv={art === null} onClick={() => setArt(null)}>Alle <Zahl n={Object.values(ergebnis.anzahl).reduce((a, b) => a + b, 0)} /></Chip>
            {SUCH_ARTEN.filter((a) => ergebnis.anzahl[a.key] > 0 || art === a.key).map((a) => (
              <Chip key={a.key} aktiv={art === a.key} onClick={() => setArt(art === a.key ? null : a.key)}>{a.label} <Zahl n={ergebnis.anzahl[a.key]} /></Chip>
            ))}
            <label className="ml-auto flex items-center gap-1.5 text-[12px] text-ink-muted">
              <input type="checkbox" checked={ohneErledigte} onChange={(e) => setOhneErledigte(e.target.checked)} className="size-3.5 accent-[var(--color-accent)]" />
              Erledigte & Archivierte ausblenden
            </label>
          </div>

          {treffer.length === 0 ? (
            <p className="py-4 text-center text-sm text-ink-faint">Nichts gefunden.</p>
          ) : (
            <ul id={listeId} role="listbox" aria-label="Treffer" className="flex flex-col gap-1">
              {treffer.map((t, i) => (
                <li
                  key={t.eintrag.key}
                  id={`${listeId}-${i}`}
                  role="option"
                  aria-selected={i === gewaehlt}
                  onClick={() => waehle(i)}
                  onMouseMove={() => setAktiv(i)}
                  className={cn(
                    'flex cursor-pointer flex-col gap-0.5 rounded-md border px-3 py-2',
                    i === gewaehlt ? 'border-accent bg-accent/5' : 'border-line',
                    t.eintrag.erledigt && 'opacity-70',
                  )}
                >
                  <span className="text-[11px] text-ink-faint">{t.eintrag.was}{t.eintrag.erledigt ? ' · erledigt' : ''}</span>
                  <span className="text-sm font-medium"><Markiert stuecke={t.titel} /></span>
                  {t.ausschnitt.length > 0 && <span className="text-[12px] text-ink-muted"><Markiert stuecke={t.ausschnitt} /></span>}
                  {t.eintrag.tags.length > 0 && <span className="text-[11px] text-ink-faint">{t.eintrag.tags.map((x) => `#${x}`).join(' ')}</span>}
                </li>
              ))}
            </ul>
          )}
          {ergebnis.treffer.length > MAX && (
            <p className="text-[12px] text-ink-faint">{ergebnis.treffer.length - MAX} weitere Treffer – Suchbegriff genauer fassen oder nach Art filtern.</p>
          )}
        </>
      )}
    </div>
  );
}

function Markiert({ stuecke }: { stuecke: Stueck[] }) {
  return <>{stuecke.map((s, i) => (s.treffer ? <mark key={i} className="rounded-sm bg-accent/25 px-px text-ink">{s.text}</mark> : <span key={i}>{s.text}</span>))}</>;
}

function Chip({ aktiv, onClick, children }: { aktiv: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button type="button" aria-pressed={aktiv} onClick={onClick}
      className={cn('rounded-full border px-2.5 py-0.5 text-[12px] transition-colors',
        aktiv ? 'border-accent bg-accent/10 text-ink' : 'border-line text-ink-muted hover:border-line-strong hover:text-ink')}>
      {children}
    </button>
  );
}

const Zahl = ({ n }: { n: number }) => <span className="tabular-nums text-ink-faint">{n}</span>;
